import { useContext, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { AnalysisProgressContext } from '@/contexts/analysis-progress-context'
import { MAX_CONCURRENT_BATCHES } from '@/config/batch'
import { useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { getGenerationJobs } from '@/api/requests/prompt'
import { useHallucinationProgress } from '@/hooks/features/hallucination'
import {
  useBottomBarOffset,
  PROGRESS_INDICATOR_HEIGHT_VAR,
} from '@/hooks/common/useBottomBarOffset'

export function CombinedProgressIndicator() {
  // 両方のコンテキストから状態を取得
  const { t } = useTranslation()
  const location = useLocation()
  const analysisContext = useContext(AnalysisProgressContext)
  const cardRef = useRef<HTMLDivElement>(null)

  // Derive spaceId from URL: /space/:space_id/...
  const spaceMatch = location.pathname.match(/^\/space\/([^/]+)/)
  const spaceId = spaceMatch ? spaceMatch[1] : null

  // Fetch space-scoped concurrency summary
  const { data: summary } = useQuery({
    queryKey: ['generationJobs', spaceId, 'summary-indicator'],
    queryFn: () =>
      getGenerationJobs(spaceId as string, {
        status: undefined,
        page: 1,
        limit: 1,
        sortBy: 'createdAt',
        sortOrder: 'desc',
      }),
    enabled: !!spaceId,
    staleTime: 10 * 1000,
    refetchInterval: data => {
      const running = (data as any)?.concurrency?.running || 0
      const pending = (data as any)?.concurrency?.pending || 0
      return running + pending > 0 ? 5000 : 30000
    },
  })

  const activePromptCount =
    ((summary as any)?.concurrency?.running || 0) + ((summary as any)?.concurrency?.pending || 0)
  const promptLimit = (summary as any)?.concurrency?.limit || 5
  const hasRunningPrompt = activePromptCount > 0

  // 実行中の分析バッチを取得
  const runningBatches = analysisContext
    ? Object.values(analysisContext.state.batches).filter(batch => {
        return (
          batch.status === 'RUNNING' || batch.status === 'RERUNNING' || batch.status === 'PENDING'
        )
      })
    : []

  // 検知の枠（FR-16-5）。**各行が自分の経路で取る**（合算しない＝枠は機能ごとに独立）。
  // ⚠️ 完了検出は持たない。running が減るだけで、完了の通知は実行ステータス経路の担当。
  const serviceMatch = location.pathname.match(/\/services?\/([^/]+)/)
  const serviceId = serviceMatch ? serviceMatch[1] : undefined
  const { running: runningDetections, limit: detectionLimit } = useHallucinationProgress(
    spaceId ?? undefined,
    serviceId
  )

  // どの機能も動いていなければカード自体を出さない（既存の挙動を踏襲）。
  const isVisible = activePromptCount > 0 || runningBatches.length > 0 || runningDetections > 0

  // ⚠️ **自分の高さをトーストへ知らせる**。行数で高さが変わる（プロンプト生成／分析／検知）ので、
  // 受け手が定数で避けると行の増減で静かに重なる。`isVisible` を渡すのは、カードが消えたときに
  // 変数を消すため（ref の中身が null になっても effect は再実行されないため）。
  useBottomBarOffset(cardRef, PROGRESS_INDICATOR_HEIGHT_VAR, isVisible)

  if (!isVisible) {
    return null
  }

  const maxPromptJobs = promptLimit // space-level max

  return (
    // ⚠️ 画面下端に貼るフッター（検知実行の実行ボタン等）と**重ならない位置へ逃がす**。
    // z-index を上げ下げしても、どちらかが隠れて操作できない状態は解けない。
    // 変数を出していない画面では 0 に落ちるので従来どおりの位置になる。
    <div
      ref={cardRef}
      className="fixed right-4 z-[110]"
      style={{ bottom: 'calc(1rem + var(--app-bottom-bar-height, 0px))' }}
    >
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg px-4 py-3">
        {/* プロンプト生成中の行 */}
        {activePromptCount > 0 && (
          <div className="flex items-center justify-between gap-4">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t(
                'promptManagement.generation.activeLabel',
                'Prompt generation in progress ({{current}}/{{max}})',
                {
                  current: activePromptCount,
                  max: maxPromptJobs,
                }
              )}
            </span>
            <div className="flex gap-2">
              {Array.from({ length: maxPromptJobs }, (_, index) => {
                const isActive = index < activePromptCount

                if (isActive) {
                  return (
                    <div key={index} className="relative">
                      {/* Ping ring */}
                      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                        <div className="h-5 w-5 rounded-full border border-[#646cff]/60 animate-ping" />
                      </div>
                      {/* 塗り: RUNNING がある間だけ点滅 */}
                      <div
                        className={`h-3 w-3 rounded-full bg-[#646cff] ring-1 ring-white dark:ring-gray-800 ${
                          hasRunningPrompt ? 'animate-blink' : ''
                        }`}
                      />
                    </div>
                  )
                } else {
                  // 空き枠
                  return (
                    <div
                      key={index}
                      className="h-3 w-3 rounded-full bg-gray-100 ring-1 ring-inset ring-gray-500 dark:bg-gray-700 dark:ring-gray-400"
                    />
                  )
                }
              })}
            </div>
          </div>
        )}

        {/* 両方実行中の場合の区切り線 */}
        {activePromptCount > 0 && runningBatches.length > 0 && (
          <div className="my-2 border-t border-gray-200 dark:border-gray-700" />
        )}

        {/* 分析処理中の行 */}
        {runningBatches.length > 0 && (
          <div className="flex items-center justify-between gap-4">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('analysis.execution.concurrency.runningSummary', 'Running ({{current}}/{{max}})', {
                current: runningBatches.length,
                max: MAX_CONCURRENT_BATCHES,
              })}
            </span>
            <div className="flex gap-2">
              {Array.from({ length: MAX_CONCURRENT_BATCHES }, (_, index) => {
                // ⚠️ 未使用の枠は**塗りの濃さでなく枠線**で示す（共通設計 §16.5・T-34）。
                // 塗りだけで「未塗り vs 背景 3:1」と「塗り vs 未塗り 3:1」を同時に満たせないため。
                let dotClass =
                  'bg-gray-100 ring-1 ring-inset ring-gray-500 dark:bg-gray-700 dark:ring-gray-400'

                if (index < runningBatches.length) {
                  // 実行中
                  dotClass = 'bg-[#646cff] animate-pulse'
                }

                return (
                  <div
                    key={index}
                    className={`h-4 w-4 rounded-full transition-all duration-300 ${dotClass}`}
                  />
                )
              })}
            </div>
          </div>
        )}

        {/* 分析と検知が同時に動いている場合の区切り線 */}
        {runningBatches.length > 0 && runningDetections > 0 && (
          <div className="my-2 border-t border-gray-200 dark:border-gray-700" />
        )}

        {/* ハルシネーション検知の行（FR-16-5・インジケーター①） */}
        {runningDetections > 0 && (
          <div className="flex items-center justify-between gap-4">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t(
                'hallucination.detection.concurrency.runningSummary',
                '検知実行中 ({{current}}/{{max}})',
                {
                  current: runningDetections,
                  max: detectionLimit,
                }
              )}
            </span>
            <div className="flex gap-2">
              {Array.from({ length: detectionLimit }, (_, index) => (
                <div
                  key={index}
                  className={`h-4 w-4 rounded-full transition-all duration-300 ${
                    index < runningDetections
                      ? 'bg-[#646cff] animate-pulse'
                      : 'bg-gray-100 ring-1 ring-inset ring-gray-500 dark:bg-gray-700 dark:ring-gray-400'
                  }`}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
